import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, Table } from 'reactstrap';
import { toast } from 'react-toastify';
import moment from 'moment';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';
import ChevronUpIcon from 'mdi-react/ChevronUpIcon';
import PlayIcon from 'mdi-react/PlayIcon';
import Api from '../../../util/api';
import Loading from '../../../shared/components/custom/Loading';
import FormCard from '../../../shared/components/custom/FormCard';
import SquareIconButton from '../../../shared/components/custom/SquareIconButton';
import { formatDate, formatDateTime } from '../../../util/date-and-time';
import { partial } from '../../../util/translation/translation';
import { formatKr } from '../connect';

const PROBLEM_TYPES = [
  'no_payment',
  'wrong_account',
  'no_fee',
  'unknown_payment',
  'totals_mismatch',
];

// Super-users: the checks of our Connect payments against Stripe. The daily
// one compares the last days and fills in what webhooks missed, the monthly
// one compares last month's fees per receiver. What they couldn't repair is
// listed as problems.
const ConnectChecks = ({ receiverNames }) => {
  const p = partial('connect');
  const [checks, setChecks] = useState([]);
  const [open, setOpen] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchChecks = async () => {
    setLoading(true);
    try {
      setChecks((await Api.connect.getChecks()) || []);
    } catch (err) {
      toast.error(
        typeof err === 'string' && err ? err : p('checksRetrievingERROR')
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChecks();
    // fetchChecks is redefined every render
    // eslint-disable-next-line
  }, []);

  const runCheck = async (kind) => {
    setLoading(true);
    try {
      const run = await Api.connect.runCheck(kind);
      if (!run) {
        toast.info(p('checkAlreadyRunning'));
      } else if (run.problems?.length) {
        toast.warning(p('checkDoneProblems', { count: run.problems.length }));
      } else {
        toast.success(p('checkDone'));
      }
    } catch (err) {
      toast.error(typeof err === 'string' && err ? err : p('checkRunERROR'));
    }
    await fetchChecks();
  };

  // The period's end isn't included
  const periodLabel = (check) =>
    `${formatDate(check.periodStart)} – ${formatDate(
      moment(check.periodEnd).subtract(1, 'millisecond')
    )}`;

  const problemFacts = (problem) =>
    [
      problem.transactionId &&
        p('problemTransaction', { id: problem.transactionId }),
      problem.receiverId &&
        p('problemReceiver', {
          name: receiverNames[problem.receiverId] || problem.receiverId,
        }),
      problem.stripeId && p('problemStripeId', { id: problem.stripeId }),
      (problem.ours !== undefined || problem.stripe !== undefined) &&
        p('problemAmounts', {
          ours: formatKr(problem.ours),
          stripe: formatKr(problem.stripe),
        }),
      problem.detail,
    ].filter(Boolean);

  const problemList = (check) => (
    <ul className="mb-0 pl-3">
      {check.problems.map((problem, index) => (
        // Problems have no id; the list never changes once saved
        <li key={index} className="mb-2">
          <strong>
            {PROBLEM_TYPES.includes(problem.type)
              ? p(`problem-${problem.type}`)
              : p('problem-other', { type: problem.type })}
          </strong>
          {problemFacts(problem).map((fact) => (
            <small key={fact} className="d-block text-muted">
              {fact}
            </small>
          ))}
        </li>
      ))}
    </ul>
  );

  return (
    <>
      <Loading loading={loading} />
      <FormCard
        title={p('checks')}
        actions={
          <div className="d-flex">
            <Button
              type="button"
              outline
              size="sm"
              className="mb-0 mr-2"
              disabled={loading}
              onClick={() => runCheck('daily')}
            >
              <PlayIcon size={16} /> {p('runDailyCheck')}
            </Button>
            <Button
              type="button"
              outline
              size="sm"
              className="mb-0"
              disabled={loading}
              onClick={() => runCheck('monthly')}
            >
              <PlayIcon size={16} /> {p('runMonthlyCheck')}
            </Button>
          </div>
        }
        bodyClassName="form-card__body--flush"
      >
        <Table responsive striped className="table--roomy table--stack mb-0">
          <thead>
            <tr>
              <th>{p('checkRanAt')}</th>
              <th>{p('checkKind')}</th>
              <th>{p('checkPeriod')}</th>
              <th className="text-right">{p('checkChecked')}</th>
              <th className="text-right">{p('checkRepaired')}</th>
              <th className="text-right">{p('checkProblems')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {checks.map((check) => {
              const problems = check.problems?.length || 0;
              return (
                <React.Fragment key={check.id}>
                  <tr>
                    <td>{formatDateTime(check.createdAt)}</td>
                    <td>{p(`checkKind-${check.kind}`)}</td>
                    <td>{periodLabel(check)}</td>
                    <td className="text-right">{check.checked}</td>
                    <td className="text-right">{check.repaired}</td>
                    <td className="text-right">
                      <Badge color={problems ? 'danger' : 'success'}>
                        {problems}
                      </Badge>
                    </td>
                    <td>
                      {problems > 0 && (
                        <div className="icon-actions">
                          <SquareIconButton
                            icon={
                              open[check.id] ? (
                                <ChevronUpIcon size={16} />
                              ) : (
                                <ChevronDownIcon size={16} />
                              )
                            }
                            title={
                              open[check.id]
                                ? p('hideProblems')
                                : p('showProblems')
                            }
                            onClick={() =>
                              setOpen((current) => ({
                                ...current,
                                [check.id]: !current[check.id],
                              }))
                            }
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                  {open[check.id] && problems > 0 && (
                    <tr>
                      <td colSpan={7}>{problemList(check)}</td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {!loading && !checks.length && (
              <tr>
                <td colSpan={7} className="text-center text-muted">
                  {p('noChecks')}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </FormCard>
    </>
  );
};

ConnectChecks.propTypes = {
  receiverNames: PropTypes.objectOf(PropTypes.string),
};

ConnectChecks.defaultProps = {
  receiverNames: {},
};

export default ConnectChecks;
